import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

import { Modal, ModalBody, ModalHeader, Text } from '@change/ds-core-ui';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import type { OnlineShareChannel } from 'src/app/pages/starterDashboard/shared/types';
import type { ShareAction } from 'src/app/shared/hooks/share';

import { ActionItems } from './components/ActionItems';
import { useTrackShareActionsView } from './hooks/useTrackShareActionsView';

type OnlineShareAction = ShareAction<OnlineShareChannel>;

type ModalOptions = {
	actions: readonly OnlineShareAction[];
	petitionId: string;
};

type OpenSharePetitionModalFn = (options: ModalOptions) => {
	waitForResult: () => Promise<undefined>;
};

const SharePetitionModalContext = createContext<OpenSharePetitionModalFn | null>(null);

export const SharePetitionModalProvider = SharePetitionModalContext.Provider;

export function useSharePetitionModalContext(): OpenSharePetitionModalFn {
	const ctx = useContext(SharePetitionModalContext);
	if (!ctx) throw new Error('useSharePetitionModalContext must be used within SharePetitionModalProvider');
	return ctx;
}

export function useSharePetitionModal(): [openModal: OpenSharePetitionModalFn, modal: React.ReactElement] {
	const [isOpen, setIsOpen] = useState(false);
	const [options, setOptions] = useState<ModalOptions | null>(null);
	const resolverRef = useRef<((result: undefined) => void) | null>(null);

	const { translate } = useI18n();

	const handleClose = useCallback(() => {
		setIsOpen(false);
		resolverRef.current?.(undefined);
		resolverRef.current = null;
	}, []);

	const openModal = useCallback((opts: ModalOptions): { waitForResult: () => Promise<undefined> } => {
		setOptions(opts);
		setIsOpen(true);

		/**
		 * Resolve any prior pending promise so it doesn't hang
		 * if the modal is opened multiple times.
		 */
		resolverRef.current?.(undefined);

		// oxlint-disable-next-line promise/avoid-new
		const promise = new Promise<undefined>((resolve) => {
			resolverRef.current = resolve;
		});

		return { waitForResult: async () => promise };
	}, []);

	const modal = (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			onOpenChange={(open) => {
				if (!open) handleClose();
			}}
			transformToSheet
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
		>
			<ModalHeader title={translate('corgi.petition.starter_dashboard.grow_petition.share_modal.title')} />
			<ModalBody>
				{options && <SharePetitionModalBody options={options} closeSharePetitionModalFn={handleClose} />}
			</ModalBody>
		</Modal>
	);

	return [openModal, modal];
}

type SharePetitionModalBodyProps = {
	options: ModalOptions;
	closeSharePetitionModalFn: () => void;
};

function SharePetitionModalBody({
	options: { actions, petitionId },
	closeSharePetitionModalFn,
}: SharePetitionModalBodyProps) {
	const channels = actions.map((action) => action.channel);

	useTrackShareActionsView({ channels, petitionId });

	return (
		<div className="mt-4">
			<Text>
				<Translate value="corgi.petition.starter_dashboard.grow_petition.share_modal.call_to_action" />
			</Text>

			<ActionItems actions={actions} closeSharePetitionModalFn={closeSharePetitionModalFn} />
		</div>
	);
}
